import * as React from 'react';
import { Theme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import Accordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import ExpandIcon from '@mui/icons-material/ExpandMore';
import clsx from 'clsx';
import { AccordionDetails } from './accordion-details';
import { AccordionLoader } from './accordion-loader';
import { AccordionSummary } from './accordion-summary';
import { IAccordionProps } from './accordion';
import { Box } from '../box';
import { ColorPalette } from '../config/color-palette';
import { toLowerCaseAndSpaceHyphens } from '../../utilities/string-helper';

export interface IAccordionWithLoaderProps extends IAccordionProps {
  showLoader?: boolean;
  isEditing?: boolean;
  sx?: AccordionProps['sx'];
}

const useStyles = makeStyles()((theme: Theme) =>
  ({
    summaryRoot: {
      minHeight: `${theme.spacing(6)} !important`,
    },
    summaryContent: {
      margin: '12px 0 !important',
    },
    root: {
      position: 'relative',
      transition: 'margin .1s linear',
      '&.is-expanded + .epwl:not(.is-expanded) .epwl__panel': {
        '&:before': {
          backgroundColor: 'transparent',
        },
      },
      '&:first-child .epwl__panel:before': {
        backgroundColor: 'transparent',
      },
    },
    expanded: {
      marginBottom: theme.spacing(2),
    },
    noCollapse: {
      cursor: 'default',
      opacity: '1 !important',
    },
    accordionRoot: {
      '.epwl:first-child &': {
        marginTop: 0,
      },
      '.epwl:not(:last-child) &:last-child': {
        borderRadius: 0,
      },
      '.epwl:first-child &:last-child': {
        borderTopLeftRadius: 4,
        borderTopRightRadius: 4,
      },
    },
  })
);

export function AccordionWithLoader(props: IAccordionWithLoaderProps) {
  const { classes } = useStyles();
  const { children, detailsLayout, expanded, defaultExpanded, noCollapse = false, showLoader = false, isEditing = false, sx = {}, ...rest } = props;
  const summary = children.find(c => c.type === AccordionSummary);
  const details = children.find(c => c.type === AccordionDetails);

  const sxProps = {
    ...sx,
    '& .MuiAccordionSummary-root': { pointerEvents: isEditing ? 'none' : 'auto' },
    '&.MuiPaper-root': {
      outline: `2px solid ${isEditing ? ColorPalette.PrimaryMain : 'transparent'}`,
      transition: 'outline .2s linear',
    },
  };

  const summaryValue = summary?.props?.children && typeof summary.props.children === 'string' ? summary.props.children : '';
  const accordionId = toLowerCaseAndSpaceHyphens(summaryValue);

  return (
    <Box className={`epwl ${expanded && 'is-expanded'} ${classes.root} ${expanded && classes.expanded}`}>
      <AccordionLoader show={showLoader} />
      <Accordion
        className="epwl__panel"
        classes={{
          root: classes.accordionRoot,
          expanded: classes.expanded,
        }}
        expanded={expanded}
        defaultExpanded={defaultExpanded}
        sx={sxProps}
        id={accordionId}
        {...rest}
      >
        <MuiAccordionSummary
          component="div"
          className={clsx(classes.summaryRoot, { [classes.noCollapse]: noCollapse })}
          expandIcon={noCollapse ? null : <ExpandIcon />}
          disabled={noCollapse}
        >
          {summary}
        </MuiAccordionSummary>
        <MuiAccordionDetails style={{ flexDirection: detailsLayout || 'column' }}>{details}</MuiAccordionDetails>
      </Accordion>
    </Box>
  );
}
